<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { cn } from '@/lib/utils';

import type { LayoutHeaderProps } from './types';

defineProps<LayoutHeaderProps>();

// Track scroll state for collapsing header
const isScrolled = ref(false);
const scrollThreshold = 50;
let scrollContainer: HTMLElement | Window | null = null;

// Find the scroll container (looks for data-scroll-container or overflow-y: auto/scroll)
function findScrollContainer(element: HTMLElement | null): HTMLElement | Window {
  if (!element) {return window;}

  let current = element.parentElement;
  while (current) {
    // Check for explicit marker
    if (current.hasAttribute('data-scroll-container')) {
      return current;
    }

    // Check for overflow-y: auto/scroll
    const style = window.getComputedStyle(current);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return current;
    }

    current = current.parentElement;
  }

  return window;
}

// Debounced scroll handler
const handleScroll = useDebounceFn((event: Event) => {
  const target = event.target as HTMLElement | Window;
  if (target === window) {
    isScrolled.value = window.scrollY > scrollThreshold;
  } else {
    isScrolled.value = (target as HTMLElement).scrollTop > scrollThreshold;
  }
}, 10);

onMounted(() => {
  // Find the scroll container dynamically
  const element = document.querySelector('[data-scroll-container]') as HTMLElement;
  scrollContainer = element || findScrollContainer(document.body);

  scrollContainer.addEventListener('scroll', handleScroll as EventListener, { passive: true });
});

onUnmounted(() => {
  if (scrollContainer) {
    scrollContainer.removeEventListener('scroll', handleScroll as EventListener);
  }
});
</script>

<template>
  <div
    v-if="title || description"
    :class="
      cn(
        'mb-6 transition-all duration-300',
        sticky
          ? 'sticky top-0 z-40 pt-4 bg-[linear-gradient(to_bottom,theme(colors.background)_1rem,transparent_1rem)] -mx-6 px-6'
          : '',
      )
    "
  >
    <!-- This wrapper clips the backdrop blur at the rounded corners -->
    <div
      :class="
        cn(
          'rounded-xl border border-border/60 shadow-sm overflow-hidden bg-card/60 transition-all duration-300',
          sticky ? 'md:backdrop-blur-md' : 'md:backdrop-blur-sm',
        )
      "
    >
      <header
        :class="
          cn(
            'flex items-center justify-between transition-all duration-300',
            'flex-col items-start gap-4 p-4 sm:flex-row sm:items-center',
            isScrolled && 'sm:flex-row sm:gap-4 sm:p-4 flex-row gap-3 px-4 py-2.5',
          )
        "
      >
        <div :class="cn('transition-all duration-300', isScrolled && 'sm:block hidden')">
          <h1 class="text-xl font-bold tracking-tight text-card-foreground md:text-2xl">
            {{ title }}
          </h1>
          <p
            v-if="description && !isScrolled"
            class="mt-1 text-sm text-muted-foreground transition-opacity duration-300"
          >
            {{ description }}
          </p>
        </div>
        <aside :class="cn('flex shrink-0 items-center transition-all duration-300 gap-3')">
          <slot name="actions" :is-scrolled="isScrolled" />
        </aside>
      </header>
    </div>
  </div>
</template>
